"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { SharedEventDescriptors } from "@clover-native/message-hub";
import { sendEvent, subscribe } from "@/utils/nativeMessageHub";
import { useMerchant } from "./merchantProvider";
import { useRouter } from "next13-progressbar";
import { usePathname } from "next/navigation";
import { CmaWebEvents } from "@/utils/useNativeActions";
import { determineBackAction } from "@/utils/nativeBridgeUtils";
import { saveToLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { ROUTE_GUEST_CHECKOUT } from "@/utils/constants";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { useCookies } from "react-cookie";
import {
  anchorHistoryToHome,
  getAppHistoryCount,
  getPreviousPath,
} from "@/components/nativeSideEffectManager/nativeSideEffectManager";
import { useBackOverrideRef } from "@/utils/hooks/useBackOverrideRef";

interface NativeBridgeContextType {
  source: string | null;
  cmaPrimaryColor: string | null;
  cmaPlatform: string | null;
  sendEvent: (event: string, status?: string) => void;
  setBackOverride: (overrideFn: () => void) => void;
  clearBackOverride: () => void;
  handleDeviceBack: () => void;
}

const NativeBridgeContext = createContext<NativeBridgeContextType | undefined>(
  undefined
);

export const NativeBridgeProvider = ({ children, initialState }: any) => {
  const { merchant } = useMerchant();
  const router = useRouter();
  const pathname = usePathname();
  const { openCartDrawer } = useCartDrawer();
  const [cookies, setCookie] = useCookies([
    "session",
    "gcid",
    "clover_client_id",
    "clover_app_env",
    "cmaPlatform",
  ]);
  const platform = cookies.cmaPlatform;
  const isAndroid = platform?.toLowerCase() === "android";
  const isSimpleStorefront = merchant?.merchantMeta?.selectedSetup === "Simple";

  const {
    setOverride: setBackOverride,
    clearOverride: clearBackOverride,
    invokeOverride,
  } = useBackOverrideRef();

  const handleSendEvent = useCallback((event: string, status?: string) => {
    void sendEvent(event as SharedEventDescriptors, status);
  }, []);

  const handleDeviceBack = useCallback(() => {
    if (invokeOverride()) {
      return;
    }

    const action = determineBackAction(
      pathname,
      !!isSimpleStorefront,
      merchant.isStandaloneGiftCardWebsite
    );

    if (action === "HIDE_WEB_VIEW") {
      return handleSendEvent(CmaWebEvents.HIDE_WEB_VIEW);
    }

    if (getAppHistoryCount() <= 1) {
      return router.replace("/");
    }

    const prevPath = getPreviousPath();

    if (!prevPath) {
      return router.back();
    }

    return router.replace(prevPath);
  }, [pathname, router, handleSendEvent, merchant, invokeOverride]);

  const latestBackPressHandler = useRef(handleDeviceBack);

  useEffect(() => {
    latestBackPressHandler.current = handleDeviceBack;
  }, [handleDeviceBack]);

  useEffect(() => {
    if (initialState.source !== "cma") return;

    const unsubscribeFn = subscribe("NATIVE_EVENT", (data: any) => {
      const { event, message } = data;

      switch (event) {
        case "DeviceBackPressed":
          latestBackPressHandler.current();
          break;

        case "ContinueAsGuest":
          saveToLocalStorage(LocalStorageKey.CMA_CONTINUE_AS_GUEST, true); // TODO: Remove after checkout
          router.push(ROUTE_GUEST_CHECKOUT);
          break;

        case "OpenCart":
          anchorHistoryToHome();
          openCartDrawer();
          break;

        case "RefreshSession":
          router.refresh();
          break;

        case "RollbackCart":
          globalThis.dispatchEvent(new CustomEvent("RollbackCart"));
          break;
      }
    });
    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [initialState.source]);

  const contextValue = useMemo(() => {
    return {
      ...initialState,
      sendEvent: handleSendEvent,
      setBackOverride,
      clearBackOverride,
      handleDeviceBack,
    };
  }, [initialState, handleSendEvent, handleDeviceBack]);

  return (
    <NativeBridgeContext.Provider value={contextValue}>
      {children}
    </NativeBridgeContext.Provider>
  );
};

export const useNativeBridge = () => {
  const context = useContext(NativeBridgeContext);
  if (context === undefined) {
    throw new Error(
      "useNativeBridge must be used within a NativeBridgeProvider"
    );
  }
  return context;
};
